import * as React from 'react'
import { useParams } from 'react-router-dom'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import { apiClient } from 'modules/core/lib/mobx-rest'

import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import engagementAuthorization from 'modules/engagement/lib/authorization'
import SurveyShowWrapper from 'modules/surveys/components/Show/SurveyShowWrapper'
import { useQuestionGroups } from 'modules/surveys/hooks/useQuestionGroups'
import { useSurvey } from 'modules/surveys/hooks/useSurvey'
import { useSurveyFormV1 } from 'modules/surveys/hooks/useSurveyFormV1'
import { canEditSurvey } from 'modules/surveys/lib/canEditSurvey'

import { SurveyShowContext } from './hooks'

const SurveyShowPage = () => {
  const { id } = useParams()
  const { employee: currentEmployee } = useCurrentSession()
  const { loading, form } = useSurveyFormV1(Number(id))
  const { loading: loadingSurvey, survey } = useSurvey(id)
  const { loading: loadingQuestionGroups, questionGroups } = useQuestionGroups(Number(id))
  const { moduleAvailabilities } = useEngagementContext()

  const { manageSurveys } = engagementAuthorization({
    moduleAvailabilities,
    currentEmployee,
  })
  const [canbeStarted, setCanBeStarted] = React.useState(false)

  const fetchCanBeStarted = async () => {
    const api = apiClient()
    const result = await api.get(`/surveys/v2/surveys/${id}/status`)
    setCanBeStarted(result.can_be_started)
  }

  const updateCanBeStarted = async () =>
    await asyncAction(fetchCanBeStarted, {
      errorFlash: false,
      successFlash: false,
    })

  if (loading || loadingSurvey || loadingQuestionGroups) return <Loading />

  if (!loadingSurvey && !survey) return <NotFound />

  const canEdit = survey ? canEditSurvey(survey, manageSurveys) : false

  const surveyShowContextValues = {
    form: form,
    survey: survey,
    hasSurveyV2: true,
    isAnonymous: !!survey && survey.isAnonymous,
    canbeStarted: canbeStarted,
    updateCanBeStarted: updateCanBeStarted,
    canEditSurvey: canEdit,
    loading: loadingSurvey,
  }

  return (
    <SurveyShowContext.Provider value={surveyShowContextValues}>
      <SurveyShowWrapper surveyId={Number(id)} questionGroups={questionGroups} />
    </SurveyShowContext.Provider>
  )
}

export default SurveyShowPage
